import React from 'react';
import { useGifterDetailsController } from './GifterDetails.controller';
import { Alert, ContentHeader, IntegratedGiftCard, LayoutShell, PrimaryCta } from '@apps/registry/guest/components/CheckoutDialog/components';
import { GifterDetailsSelectors } from '@apps/registry/guest/components/CheckoutDialog/routes/GifterDetails/GifterDetails.selectors';
import { useGetProductData } from '@apps/registry/guest/components/CheckoutDialog/hooks';
import { ProductSummaryWithImage } from '@apps/registry/guest/routes/GuestRegistry/components/PurchaseDialog';
import { SpacingStack, TextArea } from '@withjoy/joykit';
import { FormControl } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/FormControl';
import { Input } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/Input';
import { DefaultEmailField } from '@apps/registry/guest/components/CheckoutDialog/components/DefaultEmailField';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useRegistryHost } from '@apps/registry/common/utils/useRegistryHost';

export const GifterDetails = () => {
  const { value: registryGiftWrapCashOn } = useFeatureValue('registryGiftWrapCashEnabled');
  const { value: registryGiftWrapAffiliateOn } = useFeatureValue('registryGiftWrapAffiliateEnabled');
  const {
    errors,
    handleSubmit,
    getFieldProps,
    isDeferred,
    isCashFundType,
    translations,
    reserveItemTranslation,
    useDefaultEmail,
    userEmail,
    showCheckMailDialog,
    isAffiliate,
    formValues,
  } = useGifterDetailsController();

  const host = useRegistryHost();
  const { product, productImageSrc, currency } = useGetProductData('gifterDetails');

  const {
    title,
    cta,
    fieldNameLabel,
    fieldEmailLabel,
    fieldNoteLabel,
    fieldNamePlaceholder,
    fieldEmailPlaceholder,
    fieldNotePlaceholder,
    titleDeferred,
    warningText,
    description,
  } = translations;

  return (
    <LayoutShell {...GifterDetailsSelectors.ContainerElement}>
      <LayoutShell.Header mobilePlacement={isCashFundType ? 'float' : 'static'} />
      <LayoutShell.Body>
        <LayoutShell.MediaPanel>
          {isAffiliate ? (
            <ProductSummaryWithImage
              product={product}
              productImageSrc={productImageSrc}
              quantity={formValues.quantity}
              currency={currency}
            />
          ) : (
            <IntegratedGiftCard mobileRenderBehavior={isCashFundType ? 'fullBleed' : 'card'} />
          )}
        </LayoutShell.MediaPanel>
        <LayoutShell.ContentPanel>
          <ContentHeader>
            <ContentHeader.Hed>{isDeferred ? titleDeferred() : title()}</ContentHeader.Hed>
            {!isDeferred && <ContentHeader.Dek>{description({ host })}</ContentHeader.Dek>}
          </ContentHeader>
          <form onSubmit={handleSubmit}>
            <SpacingStack
              spacing={6}
              marginTop={8}
            >
              <FormControl
                label={fieldNameLabel()}
                isInvalid={!!errors.name}
                error={errors.name}
              >
                <Input
                  {...GifterDetailsSelectors.NameInput}
                  isInvalid={!!errors.name}
                  placeholder={fieldNamePlaceholder()}
                  {...getFieldProps('name')}
                />
              </FormControl>
              {useDefaultEmail ? (
                <DefaultEmailField
                  defaultEmailFieldLabel={userEmail!}
                  changeFieldButtonLabel={reserveItemTranslation.changeEmail()}
                  onLinkClick={() => showCheckMailDialog(userEmail!)}
                />
              ) : (
                <FormControl
                  label={fieldEmailLabel()}
                  isInvalid={!!errors.email}
                  error={errors.email}
                >
                  <Input
                    {...GifterDetailsSelectors.EmailInput}
                    isInvalid={!!errors.email}
                    placeholder={fieldEmailPlaceholder()}
                    {...getFieldProps('email')}
                  />
                </FormControl>
              )}
              {!(registryGiftWrapCashOn === 'on' && isCashFundType) && !(registryGiftWrapAffiliateOn === 'on' && isAffiliate) && (
                <FormControl
                  label={fieldNoteLabel()}
                  isInvalid={!!errors.note}
                  error={errors.note}
                >
                  <TextArea
                    {...GifterDetailsSelectors.NoteInput}
                    placeholder={fieldNotePlaceholder()}
                    resize={false}
                    minRows={3}
                    maxRows={3}
                    {...getFieldProps('note')}
                  />
                </FormControl>
              )}
              {isDeferred && <Alert description={warningText()} />}
            </SpacingStack>
            <PrimaryCta
              {...GifterDetailsSelectors.SubmitButton}
              marginTop={7}
              fullWidth
              type="submit"
            >
              {cta()}
            </PrimaryCta>
          </form>
        </LayoutShell.ContentPanel>
      </LayoutShell.Body>
    </LayoutShell>
  );
};
